import React, { useState } from 'react';
import { cn } from "@/lib/utils";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Search, CheckCircle, Filter } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { SummaryStats } from "../types";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ComparisonFilters } from "@/hooks/useComparisonState";

interface StickyComparisonHeaderProps {
    stats: SummaryStats;
    filters: ComparisonFilters;
    onSearchChange: (query: string) => void;
    onAddSearchTerm: (term?: string) => void;
    onRemoveSearchTerm: (term: string) => void;
    onClearSearchTerms: () => void;
    onApproveHighConfidence: () => void;
    onToggleShowMatched?: () => void;
    onToggleHideEmptyRooms?: () => void;
    onSetConfidenceLevel?: (level: 'all' | 'high' | 'medium' | 'low') => void;
    onToggleShowOverscoped?: () => void;
    onToggleShowAdditional?: () => void;
    onToggleShowDiscrepancy?: () => void;
    onToggleShowNotOnInsurance?: () => void;
    onSetSpecialFilter?: (filter: 'overscoped' | 'additional' | 'discrepancy' | 'notOnInsurance' | 'none') => void;
    className?: string;
    hideConfidenceFilters?: boolean;
    top?: string;
}

export const StickyComparisonHeader: React.FC<StickyComparisonHeaderProps> = ({
    stats,
    filters,
    onAddSearchTerm,
    onRemoveSearchTerm,
    onClearSearchTerms,
    onApproveHighConfidence,
    onToggleShowMatched,
    onToggleHideEmptyRooms,
    onSetConfidenceLevel,
    onToggleShowOverscoped,
    onToggleShowAdditional,
    onToggleShowDiscrepancy,
    onToggleShowNotOnInsurance,
    onSetSpecialFilter,
    className,
    hideConfidenceFilters,
    top = "top-0"
}) => {
    const [localQuery, setLocalQuery] = useState("");

    const handleSubmit = (e?: React.FormEvent | React.KeyboardEvent | React.MouseEvent) => {
        // Prevent form submission which causes flashing/reload
        if (e && e.type === 'submit') e.preventDefault();

        if (!localQuery.trim()) return;

        // Pass the local term directly to avoid race conditions with global state
        onAddSearchTerm(localQuery);
        setLocalQuery('');
    };

    return (
        <div className={cn("pb-2 bg-slate-50 dark:bg-slate-950", top !== "static" && "sticky z-20", top !== "static" && top, className)}>
            <Card className="rounded-b-xl border-t p-3 shadow-sm max-sm:p-2.5">
                <div className="flex flex-col items-start justify-between gap-4 md:flex-row max-sm:gap-3">
                    {/* Search Bar */}
                    <div className="w-full md:w-1/3">
                        <div className="relative">
                            <Button
                                variant="ghost"
                                size="icon"
                                className="absolute left-1 top-1/2 transform -translate-y-1/2 h-8 w-8 text-muted-foreground hover:text-primary z-10"
                                onClick={(e) => handleSubmit(e)}
                            >
                                <Search className="h-4 w-4" />
                            </Button>
                            <Input
                                placeholder={filters.searchTerms.length > 0 ? "Add another term..." : "Search rooms, items..."}
                                value={localQuery}
                                onChange={(e) => setLocalQuery(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleSubmit(e);
                                    }
                                }}
                                className="pl-10 bg-background/50"
                            />
                        </div>
                        {(filters.searchTerms.length > 0 || !filters.showMatched || filters.confidenceLevel !== 'all' || filters.hideEmptyRooms || filters.showOverscoped || filters.showAdditional || filters.showDiscrepancy || filters.showNotOnInsurance) && (
                            <div className="flex flex-wrap gap-2 mt-2">
                                {filters.searchTerms.map((term, i) => (
                                    <Badge key={`term-${i}`} variant="secondary" className="flex items-center gap-1 px-2 py-0.5 text-xs font-normal">
                                        "{term}"
                                        <button
                                            onClick={() => onRemoveSearchTerm(term)}
                                            className="ml-1 hover:text-destructive focus:outline-none"
                                        >
                                            <span className="sr-only">Remove</span>
                                            &times;
                                        </button>
                                    </Badge>
                                ))}

                                {!filters.showMatched && (
                                    <Badge variant="secondary" className="flex items-center gap-1 px-2 py-0.5 text-xs font-normal border-amber-200 bg-amber-50 text-amber-900 border hover:bg-amber-100">
                                        Hiding Matched
                                        <button
                                            onClick={() => onToggleShowMatched?.()}
                                            className="ml-1 hover:text-amber-700 focus:outline-none font-bold"
                                        >
                                            <span className="sr-only">Remove</span>
                                            &times;
                                        </button>
                                    </Badge>
                                )}

                                {filters.hideEmptyRooms && (
                                    <Badge variant="secondary" className="flex items-center gap-1 px-2 py-0.5 text-xs font-normal border-amber-200 bg-amber-50 text-amber-900 border hover:bg-amber-100">
                                        Hiding Empty Rooms
                                        <button
                                            onClick={() => onToggleHideEmptyRooms?.()}
                                            className="ml-1 hover:text-amber-700 focus:outline-none font-bold"
                                        >
                                            <span className="sr-only">Remove</span>
                                            &times;
                                        </button>
                                    </Badge>
                                )}

                                {filters.confidenceLevel !== 'all' && (
                                    <Badge variant="secondary" className="flex items-center gap-1 px-2 py-0.5 text-xs font-normal border-blue-200 bg-blue-50 text-blue-900 border hover:bg-blue-100">
                                        {filters.confidenceLevel === 'high' ? 'High Confidence Only' :
                                            filters.confidenceLevel === 'medium' ? 'Medium Confidence Only' :
                                                'Low Confidence Only'}
                                        <button
                                            onClick={() => onSetConfidenceLevel?.('all')}
                                            className="ml-1 hover:text-blue-700 focus:outline-none font-bold"
                                        >
                                            <span className="sr-only">Remove</span>
                                            &times;
                                        </button>
                                    </Badge>
                                )}

                                {hideConfidenceFilters && (
                                    <>
                                        {filters.showOverscoped && (
                                            <Badge className="flex items-center gap-1 px-2 py-0.5 text-xs font-normal border-0 bg-gradient-to-r from-green-600 to-emerald-600 text-white shadow-sm hover:from-green-700 hover:to-emerald-700">
                                                Overscoped Items
                                                <button onClick={() => onToggleShowOverscoped?.()} className="ml-1 hover:text-white/80 focus:outline-none font-bold">
                                                    <span className="sr-only">Remove</span>&times;
                                                </button>
                                            </Badge>
                                        )}
                                        {filters.showAdditional && (
                                            <Badge className="flex items-center gap-1 px-2 py-0.5 text-xs font-normal border-0 bg-gradient-to-r from-purple-600 to-violet-600 text-white shadow-sm hover:from-purple-700 hover:to-violet-700">
                                                Additional Items
                                                <button onClick={() => onToggleShowAdditional?.()} className="ml-1 hover:text-white/80 focus:outline-none font-bold">
                                                    <span className="sr-only">Remove</span>&times;
                                                </button>
                                            </Badge>
                                        )}
                                        {filters.showDiscrepancy && (
                                            <Badge className="flex items-center gap-1 px-2 py-0.5 text-xs font-normal border-0 bg-gradient-to-r from-orange-600 to-amber-600 text-white shadow-sm hover:from-orange-700 hover:to-amber-700">
                                                Price Discrepancy
                                                <button onClick={() => onToggleShowDiscrepancy?.()} className="ml-1 hover:text-white/80 focus:outline-none font-bold">
                                                    <span className="sr-only">Remove</span>&times;
                                                </button>
                                            </Badge>
                                        )}
                                        {filters.showNotOnInsurance && (
                                            <Badge className="flex items-center gap-1 px-2 py-0.5 text-xs font-normal border-0 bg-gradient-to-r from-red-600 to-rose-600 text-white shadow-sm hover:from-red-700 hover:to-rose-700">
                                                Not on Insurance
                                                <button onClick={() => onToggleShowNotOnInsurance?.()} className="ml-1 hover:text-white/80 focus:outline-none font-bold">
                                                    <span className="sr-only">Remove</span>&times;
                                                </button>
                                            </Badge>
                                        )}
                                    </>
                                )}

                                {filters.searchTerms.length > 1 && (
                                    <button onClick={onClearSearchTerms} className="text-[10px] text-muted-foreground underline ml-1 hover:text-primary">
                                        Clear terms
                                    </button>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Stats Summary */}
                    <div className="flex gap-6 text-sm max-sm:grid max-sm:w-full max-sm:grid-cols-2 max-sm:gap-3">
                        <div className="flex flex-col">
                            <span className="text-muted-foreground text-xs uppercase font-bold tracking-wider">Total Diff</span>
                            <span className={stats.totalDifference === 0 ? "text-foreground font-medium" : stats.totalDifference > 0 ? "text-orange-600 font-bold" : "text-green-600 font-bold"}>
                                {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(stats.totalDifference)}
                            </span>
                        </div>
                        <div className="flex flex-col">
                            <span className="text-muted-foreground text-xs uppercase font-bold tracking-wider">Matched</span>
                            <div className="flex items-center gap-2">
                                <span className="font-medium">{stats.matchedCount} Rooms</span>
                                <Badge variant="secondary" className="text-[10px] h-4 px-1">{stats.percentDifference.toFixed(1)}% var</Badge>
                            </div>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 max-sm:w-full max-sm:flex-wrap">
                        <Popover>
                            <PopoverTrigger asChild>
                                <Button variant="outline" size="sm" className="h-9 gap-2 max-sm:w-full">
                                    <Filter className="h-3.5 w-3.5" />
                                    Filters
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-56 p-4 max-sm:w-[calc(100vw-2rem)] max-sm:max-w-sm" align="end">
                                <div className="space-y-4">
                                    {hideConfidenceFilters && (
                                        <div className="space-y-2">
                                            <h4 className="font-medium leading-none text-sm text-muted-foreground">Match Type</h4>
                                            <div className="flex items-center space-x-2">
                                                <Checkbox
                                                    id="show-matched"
                                                    checked={!filters.showMatched}
                                                    onCheckedChange={() => onToggleShowMatched?.()}
                                                />
                                                <Label htmlFor="show-matched" className="cursor-pointer">Hide Matched Line Items</Label>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <Checkbox
                                                    id="hide-empty"
                                                    checked={filters.hideEmptyRooms}
                                                    onCheckedChange={() => onToggleHideEmptyRooms?.()}
                                                />
                                                <Label htmlFor="hide-empty" className="cursor-pointer">Hide Empty Rooms</Label>
                                            </div>
                                        </div>
                                    )}

                                    {!hideConfidenceFilters && (
                                        <div className="space-y-2">
                                            <h4 className="font-medium leading-none text-sm text-muted-foreground">Confidence Level</h4>
                                            <RadioGroup
                                                value={filters.confidenceLevel}
                                                onValueChange={(val) => onSetConfidenceLevel?.(val as any)}
                                            >
                                                <div className="flex items-center space-x-2">
                                                    <RadioGroupItem value="all" id="conf-all" />
                                                    <Label htmlFor="conf-all">All</Label>
                                                </div>
                                                <div className="flex items-center space-x-2">
                                                    <RadioGroupItem value="high" id="conf-high" />
                                                    <Label htmlFor="conf-high" className="flex items-center gap-2">
                                                        High <Badge variant="outline" className="text-[10px] py-0 h-4 border-green-500 text-green-600">80%+</Badge>
                                                    </Label>
                                                </div>
                                                <div className="flex items-center space-x-2">
                                                    <RadioGroupItem value="medium" id="conf-med" />
                                                    <Label htmlFor="conf-med" className="flex items-center gap-2">
                                                        Medium <Badge variant="outline" className="text-[10px] py-0 h-4">50-80%</Badge>
                                                    </Label>
                                                </div>
                                                <div className="flex items-center space-x-2">
                                                    <RadioGroupItem value="low" id="conf-low" />
                                                    <Label htmlFor="conf-low" className="flex items-center gap-2">
                                                        Low <Badge variant="outline" className="text-[10px] py-0 h-4 border-red-200 text-red-500">&lt;50%</Badge>
                                                    </Label>
                                                </div>
                                            </RadioGroup>
                                        </div>
                                    )}

                                    {hideConfidenceFilters && (
                                        <div className="space-y-2">
                                            <h4 className="font-medium leading-none text-sm text-muted-foreground">Special Filters</h4>
                                            <RadioGroup
                                                value={filters.showOverscoped ? 'overscoped' :
                                                    filters.showAdditional ? 'additional' :
                                                        filters.showDiscrepancy ? 'discrepancy' :
                                                            filters.showNotOnInsurance ? 'notOnInsurance' : 'none'}
                                                onValueChange={(val) => onSetSpecialFilter?.(val as any)}
                                            >
                                                <div className="flex items-center space-x-2">
                                                    <RadioGroupItem value="none" id="special-none" />
                                                    <Label htmlFor="special-none" className="cursor-pointer">All Items</Label>
                                                </div>
                                                <div className="flex items-center space-x-2">
                                                    <RadioGroupItem value="overscoped" id="show-overscoped" />
                                                    <Label htmlFor="show-overscoped" className="cursor-pointer">Overscoped Items</Label>
                                                </div>
                                                <div className="flex items-center space-x-2">
                                                    <RadioGroupItem value="additional" id="show-additional" />
                                                    <Label htmlFor="show-additional" className="cursor-pointer">Additional Items</Label>
                                                </div>
                                                <div className="flex items-center space-x-2">
                                                    <RadioGroupItem value="discrepancy" id="show-discrepancy" />
                                                    <Label htmlFor="show-discrepancy" className="cursor-pointer">Price Discrepancy</Label>
                                                </div>
                                                <div className="flex items-center space-x-2">
                                                    <RadioGroupItem value="notOnInsurance" id="show-not-on-insurance" />
                                                    <Label htmlFor="show-not-on-insurance" className="cursor-pointer">Not on Insurance</Label>
                                                </div>
                                            </RadioGroup>
                                        </div>
                                    )}
                                </div>
                            </PopoverContent>
                        </Popover>

                        {!hideConfidenceFilters && (
                            <Button
                                size="sm"
                                className="h-9 gap-2 bg-green-600 hover:bg-green-700 text-white max-sm:w-full"
                                onClick={onApproveHighConfidence}
                                disabled={stats.highConfidenceCount === 0}
                            >
                                <CheckCircle className="h-3.5 w-3.5" />
                                Approve High Confidence
                            </Button>
                        )}
                    </div>
                </div>
            </Card>
        </div>
    );
};
